import * as React from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
	CurrencyRateConfigurationLockMode,
	getCurrencyRateConfigurationLockModeName,
	ICurrencyRateConfigurationList,
	ICurrencyRateConfigurationUpdate,
} from '../../../types';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { DatePicker } from '@mui/lab';
import { parse, format } from 'date-fns';
import { es } from 'date-fns/locale';

type CurrencyRateConfigEditModalProps = {
	open: boolean;
	row: ICurrencyRateConfigurationList;
	baseRate?: number; // si no viene, el campo spread queda deshabilitado
	effectiveRate?: number;
	onClose: (result: ICurrencyRateConfigurationUpdate | null) => void;
};

type CurrencyRateConfigEditModalFormValues = {
	spread: string; // lo manejamos como string para controlar decimales
	lockMode: CurrencyRateConfigurationLockMode;
	lockUntil?: string;
};

export const roundHalfUp = (value: number, decimals = 2): number => {
	if (!Number.isFinite(value)) return value;
	const factor = 10 ** decimals;
	const y = value * factor;
	// HALF_UP: positivos -> floor(y + 0.5), negativos -> -floor(-y + 0.5)
	return y >= 0 ? Math.floor(y + 0.5 + Number.EPSILON) / factor : -Math.floor(-y + 0.5 + Number.EPSILON) / factor;
};
const toISODate = (s?: string) => (s ? (s.includes('T') ? s.slice(0, 10) : s) : '');

// --- Máscara: mantiene hasta 4 decimales y un solo punto ---
const normalizeSpreadInput = (raw: string): string => {
	if (!raw) return '';
	// Solo dígitos y '.'
	let v = raw.replace(/[^\d.]/g, '');

	// Si empieza con '.', anteponer 0
	if (v.startsWith('.')) v = '0' + v;

	// Mantener solo el primer punto
	const firstDot = v.indexOf('.');
	if (firstDot !== -1) {
		const intPart = v.slice(0, firstDot);
		const decPart = v.slice(firstDot + 1).replace(/\./g, ''); // quitar puntos extra
		v = intPart + '.' + decPart.slice(0, 4); // limitar a 4 decimales
	} else {
		// Sin punto: solo dígitos (permitir '0', '10', etc.)
		// Opcional: quitar ceros a la izquierda (excepto si es solo '0')
		v = v.replace(/^0+(?=\d)/, '');
	}

	return v;
};

export const CurrencyRateConfigurationEditModal = ({
	open,
	row,
	baseRate,
	effectiveRate,
	onClose,
}: CurrencyRateConfigEditModalProps): JSX.Element => {
	const {
		control,
		handleSubmit,
		setValue,
		reset,
		formState: { errors },
	} = useForm<CurrencyRateConfigEditModalFormValues>({
		defaultValues: {
			spread: row.spread != null ? String(row.spread) : '',
			lockMode: row.lockMode,
			lockUntil: toISODate(row.lockUntil),
		},
		mode: 'onChange',
	});
	// Reset al abrir con valores de la row recibida
	React.useEffect(() => {
		if (open) {
			reset({
				spread: row.spread != null ? String(row.spread) : '',
				lockMode: row.lockMode,
				lockUntil: toISODate(row.lockUntil),
			});
		}
	}, [open, row, reset]);

	// Watch para cálculos/condiciones
	const lockMode = useWatch({ control, name: 'lockMode' });
	const spreadStr = useWatch({ control, name: 'spread' });

	// Si cambia el lockMode a algo distinto de Until, limpiar lockUntil
	React.useEffect(() => {
		if (lockMode !== CurrencyRateConfigurationLockMode.Until) {
			setValue('lockUntil', '');
		}
	}, [lockMode, setValue]);

	// Cálculo de effective rate (informativo)
	const spreadNum = spreadStr !== '' && !Number.isNaN(Number(spreadStr)) ? Number(spreadStr) : undefined;
	const effectivePreview =
		baseRate != null && spreadNum != null ? roundHalfUp(baseRate * (1 + spreadNum), 2) : undefined;

	const onSubmit = (data: CurrencyRateConfigEditModalFormValues) => {
		const payload: ICurrencyRateConfigurationUpdate = {
			coinId: row.coinId,
			spread: Number(data.spread),
			lockMode: data.lockMode,
			lockUntil: data.lockMode === CurrencyRateConfigurationLockMode.Until ? data.lockUntil : undefined,
		};
		onClose(payload);
	};

	const onCancel = () => onClose(null);

	const handleDialogClose = (_event: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
		if (reason === 'backdropClick') return; // <-- no cerrar por click afuera
		onCancel();
	};

	return (
		<Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
			<DialogTitle>Modificar configuración de {row.coinCode}</DialogTitle>

			<DialogContent dividers>
				<Stack spacing={2}>
					<Stack spacing={0.5}>
						<Typography variant="body2" color="text.secondary">
							Base rate actual: {baseRate != null ? baseRate : '—'}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Effective rate actual: {effectiveRate != null ? effectiveRate.toFixed(2) : '—'}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Effective rate (luego de guardar): {effectivePreview != null ? effectivePreview.toFixed(2) : '—'}
						</Typography>
					</Stack>

					{/* SPREAD (con máscara) */}
					<Controller
						name="spread"
						control={control}
						rules={{
							required: baseRate != null ? 'Spread requerido' : false,
							validate: (value) => {
								if (baseRate == null) return true; // si no hay baseRate, no validamos spread
								if (value === '' || value == null) return 'Spread requerido';
								const spreadPattern = /^\d+(\.\d{1,4})?$/; // hasta 4 decimales
								if (!spreadPattern.test(value)) return 'Hasta 4 decimales (ej: 0.1234)';
								const n = Number(value);
								if (Number.isNaN(n) || n < 0) return 'Debe ser un número mayor o igual a 0';
								return true;
							},
						}}
						render={({ field }) => (
							<TextField
								{...field}
								label="Spread"
								type="text"
								inputProps={{ inputMode: 'decimal', pattern: '\\d+(\\.\\d{1,4})?' }}
								helperText={errors.spread ? errors.spread.message : 'Ej.: 0.1234 (máx. 4 decimales)'}
								error={!!errors.spread}
								required={baseRate != null}
								fullWidth
								disabled={baseRate == null}
								onChange={(e) => {
									const next = normalizeSpreadInput(e.target.value);
									field.onChange(next);
								}}
								onBlur={(e) => {
									// si termina con '.', lo quitamos
									let v = e.target.value;
									if (v.endsWith('.')) v = v.slice(0, -1);
									// asegurar normalización final
									v = normalizeSpreadInput(v);
									field.onChange(v);
									field.onBlur();
								}}
							/>
						)}
					/>

					{/* LOCK MODE */}
					<Controller
						name="lockMode"
						control={control}
						render={({ field }) => (
							<TextField select label="Modo de bloqueo" fullWidth {...field}>
								<MenuItem value={CurrencyRateConfigurationLockMode.None}>
									{getCurrencyRateConfigurationLockModeName(CurrencyRateConfigurationLockMode.None)}
								</MenuItem>
								<MenuItem value={CurrencyRateConfigurationLockMode.Until}>
									{getCurrencyRateConfigurationLockModeName(CurrencyRateConfigurationLockMode.Until)}
								</MenuItem>
								<MenuItem value={CurrencyRateConfigurationLockMode.Permanent}>
									{getCurrencyRateConfigurationLockModeName(CurrencyRateConfigurationLockMode.Permanent)}
								</MenuItem>
							</TextField>
						)}
					/>

					{/* LOCK UNTIL (solo si Until) */}
					{lockMode === CurrencyRateConfigurationLockMode.Until && (
						<LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
							<Controller
								name="lockUntil"
								control={control}
								rules={{
									validate: (value) =>
										lockMode !== CurrencyRateConfigurationLockMode.Until || value ? true : 'Seleccioná una fecha',
								}}
								render={({ field }) => (
									<DatePicker
										label="Bloqueado hasta"
										inputFormat="dd/MM/yyyy"
										disablePast
										value={field.value ? parse(field.value, 'yyyy-MM-dd', new Date()) : null} // ✅ local
										onChange={(d) => field.onChange(d ? format(d, 'yyyy-MM-dd') : '')} // ✅ guarda Y-M-D sin TZ
										renderInput={(params) => (
											<TextField
												{...params}
												required
												fullWidth
												error={!!errors.lockUntil}
												helperText={errors.lockUntil?.message}
											/>
										)}
									/>
								)}
							/>
						</LocalizationProvider>
					)}
				</Stack>
			</DialogContent>

			<DialogActions>
				<Button onClick={onCancel} color="inherit">
					Cancelar
				</Button>
				<Button onClick={handleSubmit(onSubmit)} variant="contained">
					Guardar
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default CurrencyRateConfigurationEditModal;
